const CACHE_NAME = 'apollo-fabric-images-v1';
const META_PREFIX = 'apollo-fabric-gallery:';
const blobUrls = new Map();

const canUseCacheApi = () => typeof window !== 'undefined' && 'caches' in window;

// w: optional server-side resize width (128|256|512|768|1024) for storage-hosted images
export const resolveImageUrl = (src, w) => {
  if (!src) return null;
  if (!src.startsWith('/')) return src;
  const url = `${process.env.REACT_APP_BACKEND_URL}${src}`;
  if (w && src.startsWith('/api/storage/files/') && /\.(jpe?g|png|webp)$/i.test(src)) {
    return `${url}?w=${w}`;
  }
  return url;
};

// Returns a URL to render: object URL from Cache API when available, otherwise the network URL.
export const getCachedImage = async (src, w = 512) => {
  const url = resolveImageUrl(src, w);
  if (!url) return null;
  if (blobUrls.has(url)) return blobUrls.get(url);
  if (!canUseCacheApi()) return url;

  try {
    const cache = await caches.open(CACHE_NAME);
    let response = await cache.match(url);
    if (!response) {
      response = await fetch(url, { mode: 'cors' });
      if (!response.ok) return url;
      await cache.put(url, response.clone());
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    blobUrls.set(url, objectUrl);
    return objectUrl;
  } catch {
    return url;
  }
};

export const evictCachedImage = async (src) => {
  const url = resolveImageUrl(src);
  if (!url) return;
  const objectUrl = blobUrls.get(url);
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    blobUrls.delete(url);
  }
  if (canUseCacheApi()) {
    const cache = await caches.open(CACHE_NAME);
    await cache.delete(url);
  }
};

export const readGalleryCache = (key) => {
  try {
    const raw = localStorage.getItem(META_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const MAX_GALLERY_CACHE_CHARS = 400000; // keep admin gallery caches from filling the shared localStorage quota

const purgeGalleryCache = () => {
  try {
    Object.keys(localStorage).filter(k => k.startsWith(META_PREFIX)).forEach(k => localStorage.removeItem(k));
  } catch { /* storage disabled */ }
};

export const writeGalleryCache = (key, data) => {
  let raw;
  try { raw = JSON.stringify(data); } catch { return; }
  if (raw.length > MAX_GALLERY_CACHE_CHARS) return;
  try {
    localStorage.setItem(META_PREFIX + key, raw);
  } catch {
    purgeGalleryCache();
    try { localStorage.setItem(META_PREFIX + key, raw); } catch { /* still full — skip caching */ }
  }
};

// One-time cleanup: drop oversized gallery caches left by earlier versions
try {
  Object.keys(localStorage).filter(k => k.startsWith(META_PREFIX) && (localStorage.getItem(k) || '').length > MAX_GALLERY_CACHE_CHARS).forEach(k => localStorage.removeItem(k));
} catch { /* storage disabled */ }
